import React from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'

import { ExpandableBlocks, ContentBlock } from 'components/ContentBlock'
import { Form, Select, Text, Checkbox, TextEditor, Number } from 'components/forms'
import { listRecipes, listProductLabelGroups, getFAProduct } from '../../../gql/query'
import { updateProductProperties } from '../../../gql/mutation'
import { useMutation } from 'components/graphql'
import { useQuery } from 'components/graphql'
import Divider from 'components/Divider'
import { minMaxFields } from 'helpers/validators'
// import updateProperties from '../../../fragments/mutateProductProperties'

const General = ({ data = {}, itemId, formData, mutations: { updateItem } }) => {
	const [t] = useTranslation()
	const [updateProperties] = useMutation(updateProductProperties)
	const cOptions = categoriesOptions(t)

	const isFAUsed = data?.FA_ProductId
	const isRecipeUsed = data?.RA_RecipeId

	const { data: faProductData, queryField: faField } = useQuery(getFAProduct, {
		variables: { id: data?.FA_ProductId },
		skip: !isFAUsed || isRecipeUsed
	})
	const faProduct = faProductData?.[faField]

	const weightField = (isRecipeUsed && !isFAUsed)
		? 'RA_Recipe.weight'
		: (isFAUsed && faProduct)
			? 'faProduct.salesWeight'
			: 'weight'

	const handleFormChange = ({ properties, ...vals }) => {
		const { RA_Recipe: RA_Recipe_form, faProduct: faVals, ...mutateVals } = vals
		const { ingredients = [], linkProduct = {}, RA_Recipe = {}, ...restData } = data || {}

		updateProperties({
			variables: {
				productId: data.id,
				fields: cOptions.reduce((acc, opt) => {
					const setProperty = properties.find(prop => prop.value === opt.value)
					acc[opt.value] = setProperty ? true : false
					return acc
				}, {})
			}
		})
		updateItem(mutateVals, {
			optimisticResponse: {
				cashAssist: {
					updateProduct: {
						...restData,
						ingredients,
						linkProduct,
						RA_Recipe,
						...mutateVals,
						__typename: 'Product'
					},
					__typename: 'CA_Mutation'
				},
				__typename: 'Mutation'
			}
		})
	}

	const getConsumeField = keepCool => {
		const keepCoolLabel = t('product_section.fields.consume_till')
		const noKeepCoolLabel = t('product_section.fields.shelf_life_days')
		const out = {
			field: '',
			label: keepCool ? keepCoolLabel : noKeepCoolLabel
		}
		if(isRecipeUsed) {
			const raKeepCool = data?.RA_Recipe?.mustBeKeptCool
			out.label = raKeepCool ? keepCoolLabel : noKeepCoolLabel
			out.field = raKeepCool ? 'RA_Recipe.shelfLifeInCool' : 'RA_Recipe.shelfLife'
		} else if (isFAUsed) {
			out.field = keepCool ? 'faProduct.consumeDays' : 'faProduct.helfLifeDays'
		} else {
			out.field = keepCool ? 'daysToConsume' : 'shelfLife'
		}
		return out
	}

	return(
		<Form
			initialValues={{
				...data,
				...faProduct && { faProduct },
				properties: cOptions.filter(opt => data.properties?.[opt.value])
			}}
			onSubmit={vals => handleFormChange(vals, updateItem)}
			reinitByModVer
			smartSubmit
			updateByInitial
		>
			{({ formState }) => (
				<ExpandableBlocks nested>
					<ContentBlock startExpanded label={t('product_section.strings.general_information')}>
						<Row>
							<Col xs={6}>
								<Text field='altLabel' label={t('product_section.fields.labeling')} placeholder='Alternative Bezeichnung auf Etikett' disabled={isFAUsed} />
								<TextEditor
									style={{ maxWidth: '600px' }}
									field={isRecipeUsed ? 'RA_Recipe.calculatedVals.declaration' : 'description'}
									disabled={isFAUsed || isRecipeUsed} />
								<Checkbox
									field={isRecipeUsed ? 'RA_Recipe.mustBeKeptCool' : 'mustBeKeptCool'}
									label={t('product_section.fields.must_kept_cool')}
									disabled={isRecipeUsed}
								/>
							</Col>
							<Col xs>
								<Text
									field={isRecipeUsed ? 'RA_Recipe.storage' : 'storage'}
									label={t('product_section.fields.storage')}
									disabled={isFAUsed || isRecipeUsed}
								/>
								<Number
									field={weightField}
									label={t('product_section.fields.weight_g')}
									disabled={isFAUsed || isRecipeUsed}
									withControls={false}
									inline
								/>
								<Select
									field='properties'
									isMulti
									label={t('product_section.categories.caption')}
									simpleValue={false}
									options={cOptions}
								/>
							</Col>
							<Col xs>
								<Number
									field={isRecipeUsed ? 'RA_Recipe.sellUp' : isFAUsed ? 'faProduct.forSaleDays' : 'sellUp'}
									label={t('product_section.fields.sell_up')}
									{...minMaxFields('min', 'shelfLife', t('validators.equal_or_less', { value: `"${t('product_section.fields.shelf_life_days')}"` }))}
									inline
									disabled={isFAUsed || isRecipeUsed} />
								<Number
									key={formState?.values?.mustBeKeptCool}
									{...getConsumeField(formState?.values?.mustBeKeptCool)}
									{...minMaxFields('max', 'sellUp', t('validators.equal_or_greater', { value: `"${t('product_section.fields.sell_up')}"` }))}
									inline
									disabled={isFAUsed || isRecipeUsed} />
								<Number
									field={isRecipeUsed ? 'RA_Recipe.toMoveTill' : 'toMoveTill'}
									label={t('product_section.fields.to_move_till')}
									{...minMaxFields('min', 'toMoveTill', t('validators.equal_or_less', { value: `"${t('product_section.fields.to_move_till')}"` }))}
									inline
									disabled={isRecipeUsed} />
							</Col>
						</Row>
					</ContentBlock>
					<ContentBlock label={t('product_section.strings.customer_information')}>
						<Row bottom='xs'>
							<Col xs>
								<Text
									field={isRecipeUsed ? 'RA_Recipe.consumptionInfo' : 'consumptionInfo'}
									disabled={isRecipeUsed}
									label={t('product_section.fields.consumption_info')}
								/>
							</Col>
							<Col xs>
								<Select
									field='RA_RecipeId'
									label={t('product_section.fields.declaration_link')}
									isClearable
									valueField='id'
									labelField='name'
									remoteOptions={{
										query: listRecipes,
										variables: { params: { sort: { field: 'code', order: 'ASC' }, limit: 20 } }
									}}
									disabled={isFAUsed}
									withVirtualBody
									isSearchable
								/>
							</Col>
							<Col xs>
								<Select
									field='linkLabelGroup'
									label={t('product_section.fields.preview')}
									remoteOptions={{ query: listProductLabelGroups }}
									valueField='id'
									labelField='name'
									isClearable />
							</Col>
							<Col xs>
								<Divider />
								<Text
									field={isRecipeUsed ? 'RA_Recipe.application' : 'application'}
									label={t('product_section.fields.sales_info')}
									disabled={isRecipeUsed}
								/>
							</Col>
						</Row>
					</ContentBlock>
				</ExpandableBlocks>
			)}
		</Form>
	)
}

export default General

export const categoriesOptions = (t) => [
	{
		value: 'isLactoseFree',
		label: t('product_section.categories.LactoseFree')
	},
	{
		value: 'isAlcohol',
		label: t('product_section.categories.isAlcohol')
	},
	{
		value: 'isBio',
		label: t('product_section.categories.isBio')
	},
	{
		value: 'isVegan',
		label: t('product_section.categories.isVegan')
	},
	{
		value: 'isVegetarian',
		label: t('product_section.categories.isVegetarian')
	},
	{
		value: 'isNoGluten',
		label: t('product_section.categories.isNoGluten')
	},
	{
		value: 'isDinkel',
		label: t('product_section.categories.isDinkel')
	},
	{
		value: 'isContainGluten',
		label: t('product_section.categories.isContainGluten')
	}
]
